import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import { METRIC_VALUES_TO_LABELS } from 'constants/metricValuesToLabels';
import { getChartActions, getChartExportingOptions } from 'modules/productAnalytics/utils';
import { useRef } from 'react';
import { Skeleton } from 'ui/skeleton';
import { CampaignAnalyticsPieChart } from './CampaignAnalyticsPieChart';
import { EngagementAreaChart } from './EngagementAreaChart';
import { MetricBreakdown } from './MetricBreakdown';
import { MetricBreakdownCard } from './MetricBreakdownCard';
import { RevenueEmptyCard } from './RevenueEmptyCard';
import {
  MetricBreakdownContent,
  OverviewRowWithTwoEqualColumns,
  SkeletonWrapper,
  WideCardContainer,
  WideCardLeft,
  WideCardRight,
} from './Styled';

import { NeutralBanner } from 'ui/banner';
import { BannerBodyBase } from 'ui/banner/Banner.styles';
import {
  CAMPAIGN_REVENUE,
  ENGAGEMENT_OPENS_CLICKS,
  ENGAGEMENT_PURCHASE,
  HEIGHT_WITHOUT_DATA,
  HEIGHT_WITH_DATA,
  HEIGHT_WITH_DATA_AND_GRAPH,
  METRIC_CARD_DATA_LOOKUP,
} from '../constants';
import {
  getHighLevelMetrics,
  parseMetricsForBreakdown,
  parseMetricsForTimeSeriesGraph,
} from '../utils';

const getChartFromCardType = (cardType, statsForTimeRange, ref) => {
  const highLevelData = statsForTimeRange?.highLevel ? getHighLevelMetrics(statsForTimeRange) : [];
  const shouldShowChart = highLevelData.length > 0;

  return {
    [ENGAGEMENT_PURCHASE]: (
      <EngagementAreaChart
        data={parseMetricsForTimeSeriesGraph([CAMPAIGN_REVENUE], statsForTimeRange?.results)}
        ref={ref}
      />
    ),
    [ENGAGEMENT_OPENS_CLICKS]: shouldShowChart && (
      <CampaignAnalyticsPieChart data={highLevelData} ref={ref} />
    ),
  }[cardType];
};

const getChartForEmbedded = (cardType, statsForTimeRange, ref) => {
  const uniqueRetrieved =
    statsForTimeRange?.results?.[METRIC_VALUES_TO_LABELS.UniqueEmbeddedRetrieved]?.value;
  const uniqueClicks =
    statsForTimeRange?.results?.[METRIC_VALUES_TO_LABELS.UniqueEmbeddedClick]?.value;

  const clickChartData = [
    {
      name: 'Unique clicks',
      y: uniqueClicks,
    },
    {
      name: 'Not yet clicked',
      y: uniqueRetrieved - uniqueClicks,
    },
  ];
  return {
    [ENGAGEMENT_OPENS_CLICKS]: <CampaignAnalyticsPieChart data={clickChartData} ref={ref} />,
  }[cardType];
};

const getChartForWhatsApp = (cardType, statsForTimeRange, ref) => {
  const uniqueDelivered =
    statsForTimeRange?.results?.[METRIC_VALUES_TO_LABELS.UniqueWhatsAppDelivered]?.value || 0;
  const uniqueReads =
    statsForTimeRange?.results?.[METRIC_VALUES_TO_LABELS.UniqueWhatsAppRead]?.value || 0;

  const clickChartData = [
    {
      name: 'Read',
      y: uniqueReads,
    },
    {
      name: 'Not read',
      y: uniqueDelivered - uniqueReads,
    },
  ];

  return {
    [ENGAGEMENT_PURCHASE]: (
      <EngagementAreaChart
        data={parseMetricsForTimeSeriesGraph([CAMPAIGN_REVENUE], statsForTimeRange?.results)}
        ref={ref}
      />
    ),
    [ENGAGEMENT_OPENS_CLICKS]: <CampaignAnalyticsPieChart data={clickChartData} ref={ref} />,
  }[cardType];
};

const getGraphData = (messageMedium, statsForTimeRange, cardType, ref) => {
  if (messageMedium === EmbeddedMessage) {
    return getChartForEmbedded(cardType, statsForTimeRange, ref);
  } else if (messageMedium === WhatsApp) {
    return getChartForWhatsApp(cardType, statsForTimeRange, ref);
  } else {
    return getChartFromCardType(cardType, statsForTimeRange, ref);
  }
};

const getMetrics = (messageMedium, statsForTimeRange, cardType, ref) => ({
  overviewMetrics: parseMetricsForBreakdown(
    METRIC_CARD_DATA_LOOKUP?.[cardType]?.[messageMedium]?.values || [],
    statsForTimeRange?.results,
  ),
  graphData: getGraphData(messageMedium, statsForTimeRange, cardType, ref),
});

export const MetricChartCard = ({ messageMedium, statsForTimeRange, cardType, isLoading }) => {
  const chartRef = useRef();
  const CHART_IDS = {
    [ENGAGEMENT_PURCHASE]: 'revenueOverviewChart',
    [ENGAGEMENT_OPENS_CLICKS]: 'engagementPieChart',
  };
  const getCustomChartActions = (chartId) =>
    ({
      [ENGAGEMENT_PURCHASE]: getChartActions(chartId, chartRef),
      [ENGAGEMENT_OPENS_CLICKS]: getChartActions(chartId, chartRef),
    })[cardType];
  const chartId = CHART_IDS[cardType];
  const chartActions = getCustomChartActions(chartId);
  const { overviewMetrics, graphData } = getMetrics(
    messageMedium,
    statsForTimeRange,
    cardType,
    chartRef,
  );
  const isWhatsApp = messageMedium === WhatsApp;
  const [overviewMetricHeader, ...otherMetrics] = overviewMetrics;

  // Check if revenue data is 0
  const showEmptyState =
    cardType === ENGAGEMENT_PURCHASE &&
    overviewMetricHeader?.every((headerMetric) => headerMetric.value === 0);

  let cardHeight = graphData ? HEIGHT_WITH_DATA_AND_GRAPH : HEIGHT_WITH_DATA;
  cardHeight = showEmptyState ? HEIGHT_WITHOUT_DATA : cardHeight;

  const showMenuOptions = !!graphData && !showEmptyState;

  const metricCardHtml = (
    <MetricBreakdownCard
      title={METRIC_CARD_DATA_LOOKUP?.[cardType]?.[messageMedium]?.header ?? ''}
      data-test={`${METRIC_CARD_DATA_LOOKUP?.[cardType]?.[messageMedium]?.header}-revenue-overview`}
      menuOptions={showMenuOptions && getChartExportingOptions(chartId)}
      handleSelectionChange={showMenuOptions && ((key) => chartActions[key]())}
      fullWidth={!!graphData}
    >
      {isLoading ? (
        <SkeletonWrapper height={cardHeight} padding={24}>
          <Skeleton />
        </SkeletonWrapper>
      ) : (
        <MetricBreakdownContent noPadding={true}>
          <WideCardContainer>
            {showEmptyState ? (
              <RevenueEmptyCard />
            ) : (
              <>
                <WideCardLeft showBorder={!!graphData}>
                  <MetricBreakdown
                    metricGrid={otherMetrics}
                    headerMetric={overviewMetricHeader?.[0]}
                    horizontalLayout={true}
                  ></MetricBreakdown>
                </WideCardLeft>
                {!!graphData && (
                  <WideCardRight>
                    {graphData}
                    {isWhatsApp && cardType === ENGAGEMENT_OPENS_CLICKS && (
                      <NeutralBanner isSubtle>
                        <BannerBodyBase>
                          Only users who have “Read receipts” turned on.
                        </BannerBodyBase>
                      </NeutralBanner>
                    )}
                  </WideCardRight>
                )}
              </>
            )}
          </WideCardContainer>
        </MetricBreakdownContent>
      )}
    </MetricBreakdownCard>
  );

  return (
    <>
      {!!graphData ? (
        metricCardHtml
      ) : (
        <OverviewRowWithTwoEqualColumns>{metricCardHtml}</OverviewRowWithTwoEqualColumns>
      )}
    </>
  );
};
